import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { environment } from '#environments';
import { TelemetryEvent } from '#enums';
import { httpStatusOf, TelemetryService } from '../telemetry/telemetry.service';

export type DossierAttachMode = 'auto' | 'suggested' | 'accepted';

export interface DossierSignal {
  signalKey: string;
  attachMode: DossierAttachMode;
  context: Record<string, unknown>;
  evidence: Record<string, unknown>;
}

export interface DossierCandidate {
  candidateId: string;
  rank: number;
  /** The explanation as the agent produced it, including agent-only fields such as confidence and rationale. */
  explanation: Record<string, unknown>;
}

/** The detection side of a case. Status, owner, and decisions come from the engine and are joined on `engineCaseId`. */
export interface Dossier {
  dossierId: number;
  useCaseCode: string;
  engineCaseId: string | null;
  isSealed: boolean;
  createdDate: string;
  signals: DossierSignal[];
  candidates: DossierCandidate[];
}

/** A Dossier as the inbox lists it. */
export interface DossierSummary {
  dossierId: number;
  agentCaseId?: number | null;
  useCaseCode: string;
  engineCaseId: string;
  facility: string | null;
  createdDate: string;
  signalCount: number;
  topCandidateId: string | null;
  topCandidateTitle: string | null;
}

@Injectable({ providedIn: 'root' })
export class DossierService {
  private _http = inject(HttpClient);
  private _telemetry = inject(TelemetryService);

  /** Dossiers that have an engine case, newest first. */
  list(filter: { facilityId?: string | null; useCase?: string | null; page?: number; pageSize?: number } = {}): Observable<DossierSummary[]> {
    let params = new HttpParams();
    if (filter.page) {
      params = params.set('page', filter.page);
    }
    if (filter.pageSize) {
      params = params.set('pageSize', filter.pageSize);
    }
    if (filter.facilityId) {
      params = params.set('facilityId', filter.facilityId);
    }
    if (filter.useCase) {
      params = params.set('useCase', filter.useCase);
    }
    return this._http.get<DossierSummary[]>(`${environment.apiUrl}/dossiers`, { params });
  }

  /** The case owner accepts or rejects a Signal that arrived after the claim. The server checks ownership against the engine. */
  resolveSuggestion(engineCaseId: string, signalKey: string, accept: boolean): Observable<void> {
    const url = `${environment.apiUrl}/dossiers/by-case/${encodeURIComponent(engineCaseId)}/signals/${encodeURIComponent(signalKey)}/resolution`;
    return this._http.post(url, { accept }).pipe(map(() => undefined));
  }

  /** Emits null when no Dossier is linked to the engine case. */
  getByEngineCaseId(engineCaseId: string): Observable<Dossier | null> {
    return this._http
      .get<Dossier>(`${environment.apiUrl}/dossiers/by-case/${encodeURIComponent(engineCaseId)}`)
      .pipe(
        catchError((error: unknown) => {
          if (error instanceof HttpErrorResponse && error.status === 404) {
            return of(null);
          }
          this._telemetry.trackAppEvent(TelemetryEvent.CaseDossierLoadFailed, { engineCaseId, status: httpStatusOf(error) });
          return throwError(() => error);
        }),
      );
  }
}
